import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { FaExclamationTriangle, FaLock, FaTrash } from 'react-icons/fa';
import AccountService from '../../services/account.service';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError, showCurrency } from '../../utils';
import { Button, controlClass } from '../EntityList';

// Main entities are itemised one row per record; system-generated ones are
// summarised as counts. Nothing is individually acknowledged — the single gate
// is typing the account name, so the summary is purely informational.
const SECTIONS = [
	{ key: 'debts', labelKey: 'entity.debt.plural' },
	{ key: 'credits', labelKey: 'entity.credit.plural' },
	{ key: 'assets', labelKey: 'entity.asset.plural' },
	{ key: 'assetSales', labelKey: 'entity.assetSale.plural' },
	{ key: 'contracts', labelKey: 'entity.contract.plural' },
	{ key: 'contacts', labelKey: 'entity.contact.plural' },
	{ key: 'documents', labelKey: 'entity.document.plural' },
];

const formatAmount = (amount, currency) => {
	if (amount === undefined || amount === null) return '';
	const value = new Intl.NumberFormat('es-UY').format(amount);
	return currency ? `${showCurrency(currency)} ${value}` : value;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

/** The one-line detail under each row's title. */
const describe = (sectionKey, item, t) => {
	const parts = [];
	switch (sectionKey) {
	case 'debts':
	case 'credits':
		parts.push(formatAmount(item.amount, item.currency), formatDate(item.date));
		if (item.dueCount) parts.push(`${item.dueCount} ${t('entity.due.plural').toLowerCase()}`);
		if (item.paymentCount) parts.push(`${item.paymentCount} ${t('entity.payment.plural').toLowerCase()}`);
		break;
	case 'assets':
		parts.push(item.type, item.ubication, item.lot && `Lote ${item.lot}`);
		if (item.billCount) parts.push(`${item.billCount} ${t('entity.bill.plural').toLowerCase()}`);
		break;
	case 'assetSales':
		parts.push(formatAmount(item.amount, item.currency), formatDate(item.date));
		if (item.debtCount) parts.push(`${item.debtCount} ${t('entity.debt.plural').toLowerCase()}`);
		break;
	case 'contracts':
		parts.push(item.status, formatAmount(item.amount, item.currency));
		if (item.orphan) parts.push('sin cuenta asignada');
		break;
	case 'contacts':
		if (item.isMainContact) parts.push('contacto principal');
		break;
	case 'documents':
		parts.push(item.documentType);
		break;
	default:
		break;
	}
	return parts.filter(Boolean).join(' · ');
};

/**
 * Confirmation for the cascade account delete. Fetches the server's deletion
 * preview, summarises everything that will be destroyed, and gates the delete
 * behind typing the account name exactly.
 *
 * When the preview reports cross-account conflicts the modal renders a blocked
 * state instead: those refs are required on the far side and cannot be
 * unlinked, so the listed records have to be dealt with manually before the
 * account can be deleted at all.
 */
function AccountDeletionModal({ show, account, onClose, onDeleted }) {
	const { t } = useLabels();
	const [state, setState] = useState('loading');
	const [preview, setPreview] = useState(null);
	const [error, setError] = useState('');
	const [confirmName, setConfirmName] = useState('');

	const accountId = account?._id;
	const accountName = account?.name || '';

	const loadPreview = useCallback(async () => {
		setState('loading');
		setError('');
		try {
			const { data } = await AccountService.getDeletionPreview(accountId);
			setPreview(data);
			setState(data.blocked ? 'blocked' : 'idle');
		} catch (err) {
			setError(getError(err));
			setState('error');
		}
	}, [accountId]);

	useEffect(() => {
		if (show && accountId) loadPreview();
	}, [show, accountId, loadPreview]);

	// Reset on exit rather than on close, so the body doesn't flip back to a
	// spinner while the modal is still fading out.
	const handleExited = () => {
		setPreview(null);
		setConfirmName('');
		setState('loading');
		setError('');
	};

	const sections = useMemo(() => {
		if (!preview) return [];
		return SECTIONS.map((section) => ({
			...section,
			label: t(section.labelKey),
			items: preview.mainEntities?.[section.key] || [],
		})).filter((section) => section.items.length > 0);
	}, [preview, t]);

	const generated = preview?.generatedEntities || [];

	const nameMatches = confirmName.trim() === accountName.trim() && accountName.trim() !== '';
	const canDelete = state === 'idle' && nameMatches;

	const handleDelete = async () => {
		if (!canDelete) return;
		setState('deleting');
		try {
			await AccountService.delete(accountId);
			toast.success('Cuenta Eliminada');
			onDeleted();
		} catch (err) {
			// A 409 carries a fresh preview: the graph changed since we loaded it,
			// so re-render from the response instead of just toasting.
			const conflicts = err?.response?.data?.conflicts;
			if (err?.response?.status === 409 && conflicts) {
				setPreview(err.response.data);
				setConfirmName('');
				setState('blocked');
			} else {
				setState('idle');
			}
			toast.error(getError(err));
		}
	};

	const busy = state === 'deleting';

	return (
		<BsModal
			show={show}
			onHide={busy ? undefined : onClose}
			onExited={handleExited}
			centered
			scrollable
			backdrop={busy ? 'static' : true}
			keyboard={!busy}
			dialogClassName='tw:max-w-[600px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton={!busy}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-danger-soft tw:text-danger'>
						{state === 'blocked' ? <FaLock /> : <FaTrash />}
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('title.deleteAccount')}
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{accountName}</p>
					</div>
				</div>
			</BsModal.Header>

			{state === 'loading' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:text-[12.5px] tw:text-ink-3'>Analizando registros relacionados…</div>
				</BsModal.Body>
			)}

			{state === 'deleting' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-danger' />
					<div className='tw:font-semibold tw:text-ink-0'>{t('msg.deleteAccountDeleting')}</div>
					<div className='tw:text-[12.5px] tw:text-ink-3'>No cierre esta ventana</div>
				</BsModal.Body>
			)}

			{state === 'error' && (
				<>
					<BsModal.Body className='tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-danger'>
							{error || t('msg.deleteAccountPreviewFailed')}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button variant='primary' onClick={loadPreview}>
							Reintentar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'blocked' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-ink-1'>
							{preview?.totals?.blockedBySize
								? `La cuenta tiene ${preview.totals.documentCount} registros relacionados, más de los ${preview.totals.maxDocumentCount} que se pueden eliminar en una sola operación. Elimine primero las deudas o contratos más grandes.`
								: t('msg.deleteAccountBlocked')}
						</div>
						{(preview?.conflicts || []).length > 0 && (
							// Capped so the explanation above and the footer stay in view no
							// matter how many records are blocking.
							<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
								{preview.conflicts.map((conflict) => (
									<div
										key={`${conflict.code}:${conflict._id}`}
										className='tw:flex tw:items-start tw:gap-2.5 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2.5 tw:last:border-b-0'
									>
										<span className='tw:mt-0.5 tw:shrink-0 tw:text-warn'>
											<FaExclamationTriangle />
										</span>
										<div>
											<div className='tw:text-[13.5px] tw:font-medium tw:text-ink-0'>{conflict.label}</div>
											<div className='tw:text-[11.5px] tw:text-ink-3'>{conflict.message}</div>
										</div>
									</div>
								))}
							</div>
						)}
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							Cerrar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'idle' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<p className='tw:mb-0 tw:text-[13px] tw:text-ink-2'>
							{sections.length === 0 && generated.length === 0
								? t('msg.confirmDeleteAccount')
								: t('msg.deleteAccountIntro')}
						</p>

						{(preview?.warnings || []).map((warning) => (
							<div
								key={warning.code}
								className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'
							>
								{warning.message}
							</div>
						))}

						{sections.map((section) => (
							<div key={section.key}>
								<div className='tw:mb-2 tw:flex tw:items-center tw:justify-between'>
									<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
										{section.label}
									</span>
									<span className='tw:text-[11px] tw:text-ink-3'>{section.items.length}</span>
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{section.items.map((item) => {
										const detail = describe(section.key, item, t);
										return (
											<div
												key={item._id}
												className='tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
											>
												<div className='tw:truncate tw:text-[13.5px] tw:text-ink-0'>{item.label}</div>
												{detail && <div className='tw:text-[11.5px] tw:text-ink-3'>{detail}</div>}
											</div>
										);
									})}
								</div>
							</div>
						))}

						{generated.length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.deleteAccountGenerated')}
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{generated.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:flex-1 tw:text-[13.5px] tw:text-ink-0'>{group.label}</span>
											<span className='tw:text-[13px] tw:font-semibold tw:text-ink-1'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{(preview?.preserved || []).length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.deleteAccountPreserved')}
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface-2'>
									{preview.preserved.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:shrink-0 tw:text-ink-3'>
												<FaLock size={11} />
											</span>
											<div className='tw:flex-1'>
												<span className='tw:text-[13px] tw:text-ink-1'>{group.label}</span>
												<span className='tw:ml-2 tw:text-[11.5px] tw:text-ink-3'>{group.reason}</span>
											</div>
											<span className='tw:text-[13px] tw:text-ink-2'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-3'>
							<label htmlFor='delete-account-confirm' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
								{t('msg.deleteAccountTypeName')}{' '}
								<span className='tw:font-semibold tw:text-ink-0'>{accountName}</span>
							</label>
							<input
								id='delete-account-confirm'
								type='text'
								autoComplete='off'
								autoFocus
								value={confirmName}
								onChange={(event) => setConfirmName(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') handleDelete();
								}}
								className={`${controlClass} tw:w-full tw:px-2.5`}
							/>
							{confirmName.length > 0 && !nameMatches && (
								<div className='tw:mt-1.5 tw:text-[11.5px] tw:text-danger'>{t('msg.deleteAccountNameMismatch')}</div>
							)}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button
							variant='primary'
							icon={<FaTrash />}
							disabled={!canDelete}
							className={!canDelete ? 'tw:opacity-50' : ''}
							onClick={handleDelete}
						>
							{t('action.delete')}
						</Button>
					</BsModal.Footer>
				</>
			)}
		</BsModal>
	);
}

AccountDeletionModal.propTypes = {
	show: PropTypes.bool.isRequired,
	account: PropTypes.shape({
		_id: PropTypes.string,
		name: PropTypes.string,
	}),
	onClose: PropTypes.func.isRequired,
	onDeleted: PropTypes.func.isRequired,
};

export default AccountDeletionModal;
